import { getConfig } from '@config';
import type { ExperimentClient, Exposure, ExposureTrackingProvider } from '@amplitude/experiment-js-client';
import type { EventGroupContext } from '@shared/core/analytics/segment/eventEnrichment';
import { getPlatformProperties, getSharedContextProperties, getViewportSize } from '@shared/core/analytics/segment/eventEnrichment';
import { getAmplitudeSessionId } from '@shared/core/analytics/segment/getTrackerIds';
import { addError, addSuppressedError } from '@shared/utils/logger';
import { withWindow } from '@shared/utils/withWindow';
import { segment } from '@static/js/segment';
import { getAnalyticsId } from '@shared/core/analytics/segment/getAnalyticsId';

import { AMPLITUDE_DEVICE_ID_COOKIE_NAMES } from './amplitude.utils';

const JOY_EVENT_GROUP_KEY = 'JoyEvent';

const AMPLITUDE_EXPOSURE_FLUSH_INTERVAL_MS = 1000;

/**
 * Reads the device-id cookie the same way SSR does in fetchAmplitudeFlagsForUser.ts:
 * iterates AMPLITUDE_DEVICE_ID_COOKIE_NAMES in priority order. Used as a fallback for
 * the exposure event's device_id when the experiment SDK has no user yet
 * (the brief window before fetchUser() resolves).
 *
 * Matches SSR's identity resolution so that bucketing-identity and exposure-identity agree
 * for the same browser, even during the pre-fetchUser window. Both sites share
 * AMPLITUDE_DEVICE_ID_COOKIE_NAMES — change priority once, both pick it up.
 */
export function readAnonymousIdFromCookie(): string | undefined {
  return withWindow(globalWindow => {
    // Parse document.cookie via URLSearchParams: swap `; ` → `&` so the key=value pairs are in
    // standard form-encoded shape, and pre-escape `+` to `%2B` because URLSearchParams would
    // otherwise decode `+` as a space (legacy form-encoding rule that doesn't apply to cookies).
    // URLSearchParams handles URL-decoding automatically and tolerates values that contain '='.
    const cookieString = globalWindow.document.cookie.replace(/\+/g, '%2B').replace(/; /g, '&');
    const params = new URLSearchParams(cookieString);
    const stripSegmentQuotes = (raw: string | null): string | undefined => {
      if (raw === null) return undefined;
      // Segment wraps its anonymous id in double quotes (e.g. `"abc-123"`). Strip them so the
      // value matches what segment.getAnonymousId() returns and what SSR fetched.
      return raw.startsWith('"') && raw.endsWith('"') ? raw.slice(1, -1) : raw;
    };
    for (const name of AMPLITUDE_DEVICE_ID_COOKIE_NAMES) {
      const value = stripSegmentQuotes(params.get(name));
      if (value) return value;
    }
    return undefined;
  }, undefined);
}

type ExposurePayload = Readonly<{
  flag_key: string;
  variant?: string;
  experiment_key?: string;
}>;

export type QueuedExposure = Readonly<{
  exposure: ExposurePayload;
  time: number;
}>;

type AmplitudeEvent = Readonly<{
  event_type: string;
  user_id?: string;
  device_id?: string;
  time: number;
  session_id?: number;
  event_properties: Record<string, unknown>;
}>;

type SharedContext = Readonly<Record<string, unknown>>;

function buildContextProperties(sharedContext: SharedContext, groupContext: EventGroupContext): SharedContext {
  const contextProperties: Record<string, unknown> = {};

  Object.entries(sharedContext).forEach(([key, value]) => {
    contextProperties[`context.${key}`] = value;
  });

  Object.entries(groupContext).forEach(([key, value]) => {
    contextProperties[`context.${key}`] = value;
  });

  return contextProperties;
}

export type EnrichmentContext = Readonly<{
  analyticsId: string;
  platformProperties: Record<string, unknown>;
  viewportSize: 'mobile' | 'desktop';
  sessionId?: number;
  sharedContext: SharedContext;
  userId?: string;
  deviceId?: string;
  firebaseEventId?: string;
}>;

export function buildAmplitudeEvent(item: QueuedExposure, enrichment: EnrichmentContext): AmplitudeEvent {
  const groupContext: EventGroupContext = {
    firebaseEventId: enrichment.firebaseEventId,
  };

  const contextProperties = buildContextProperties(enrichment.sharedContext, groupContext);

  const eventProperties = {
    analyticsId: enrichment.analyticsId,
    category: '$exposure',
    ...enrichment.platformProperties,
    viewportSize: enrichment.viewportSize,
  };

  return {
    event_type: '$exposure',
    user_id: enrichment.userId,
    device_id: enrichment.deviceId,
    time: item.time,
    session_id: enrichment.sessionId,
    event_properties: {
      ...eventProperties,
      ...contextProperties,
      // Place at the end so these fields always take precedence if keys collide.
      // [Experiment] Experiment Key disambiguates re-runs of the same flag (exp-2, exp-3, ...) —
      // Amplitude's experiment-analysis filter requires it whenever a flag has been run more than once.
      '[Experiment] Flag Key': item.exposure.flag_key,
      '[Experiment] Variant': item.exposure.variant,
      '[Experiment] Experiment Key': item.exposure.experiment_key,
    },
  };
}

class ExposureQueue {
  private queue: QueuedExposure[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly flushInterval = AMPLITUDE_EXPOSURE_FLUSH_INTERVAL_MS;

  constructor(private readonly client: ExperimentClient) {
    // Flush on page unload
    withWindow(globalWindow => {
      globalWindow.addEventListener('beforeunload', () => {
        this.flush();
      });
    });

    // Flush when page becomes hidden
    withWindow(globalWindow => {
      globalWindow.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.flush();
        }
      });
    });
  }

  enqueue(item: QueuedExposure): void {
    this.queue.push(item);
    this.startTimer();
  }

  private startTimer(): void {
    if (this.flushTimer !== null) return;

    this.flushTimer = setTimeout(() => {
      this.flush();
    }, this.flushInterval);
  }

  private clearTimer(): void {
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
  }

  private flush(): void {
    this.clearTimer();

    if (this.queue.length === 0) return;

    const itemsToSend = this.queue;
    this.queue = [];

    // Fire-and-forget: sendBatch handles errors internally via handleSendError
    this.sendBatch(itemsToSend, false).catch(error => {
      addError('Failed to send exposure batch', { error });
    });
  }

  private async sendBatch(items: QueuedExposure[], isRetry: boolean): Promise<void> {
    try {
      // Only send from browser; skip on SSR
      const shouldSend = withWindow(() => true, false);
      if (!shouldSend) {
        return;
      }

      const config = getConfig();
      const user = this.client.getUser();

      // Extract firebaseEventId from experiment client groups
      const groups = user?.groups || {};
      const joyEventGroup = groups[JOY_EVENT_GROUP_KEY];
      const firebaseEventId = Array.isArray(joyEventGroup) && joyEventGroup.length > 0 && typeof joyEventGroup[0] === 'string' ? joyEventGroup[0] : undefined;

      // Fetch all enrichment context once for the entire batch
      const enrichment: EnrichmentContext = {
        analyticsId: getAnalyticsId(),
        platformProperties: getPlatformProperties(),
        viewportSize: getViewportSize(),
        sessionId: getAmplitudeSessionId(),
        sharedContext: getSharedContextProperties(),
        userId: user?.user_id,
        // Prefer the SDK-set device_id; before fetchUser() resolves, fall back to the same cookie
        // SSR used (joy_anonymous_id) so bucketing-identity and exposure-identity agree.
        // segment.getAnonymousId() remains the final safety net.
        deviceId: user?.device_id ?? readAnonymousIdFromCookie() ?? segment.getAnonymousId(),
        firebaseEventId,
      };

      const events = items.map(item => buildAmplitudeEvent(item, enrichment));

      const response = await fetch(config.amplitudeAnalyticsApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          api_key: config.amplitudeAnalyticsApiKey,
          events,
        }),
        // keepalive ensures the request completes even if the page unloads (e.g., tab close, navigation)
        // so we don't drop telemetry
        // see https://fetch.spec.whatwg.org/#http-network-or-cache-fetch
        keepalive: true,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      await this.handleSendError(error, items, isRetry);
    }
  }

  private async handleSendError(error: unknown, items: QueuedExposure[], isRetry: boolean): Promise<void> {
    addSuppressedError('Amplitude exposure batch failed', { error, eventCount: items.length });

    // We don't want to overcomplicate things by adding multiple retries & backoff logic
    // One retry is sufficient for exposure telemetry
    if (!isRetry) {
      await this.sendBatch(items, true);
    }
  }
}

export function withAmplitudeExposureTracking(createClient: (exposureTrackingProvider: ExposureTrackingProvider) => ExperimentClient): ExperimentClient {
  // Defer binding until after client/queue are created to avoid circular init concerns
  let enqueueExposure: (exposure: Exposure) => void = () => {};

  const trackExposure = (exposure: Exposure) => enqueueExposure(exposure);

  const exposureTrackingProvider: ExposureTrackingProvider = { track: trackExposure };
  const client = createClient(exposureTrackingProvider);
  const queue = new ExposureQueue(client);

  enqueueExposure = (exposure: Exposure) => {
    queue.enqueue({
      exposure: { flag_key: exposure.flag_key, variant: exposure.variant, experiment_key: exposure.experiment_key },
      time: Date.now(),
    });
  };

  return client;
}
